import React, { useState } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowRight from '@mui/icons-material/ArrowRight';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';

interface AccordionItem {
  title: React.ReactNode;
  content: React.ReactNode;
}

interface StyledAccordionListProps {
  items: AccordionItem[];
  showArrow?: boolean;
  expandedPanelIds?: string[];
  onPanelChange?: (panelId: string, expanded: boolean) => void;
  defaultExpanded?: boolean;
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled(MuiAccordionSummary)(() => ({
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: '8px',
    marginTop: 0,
    marginBottom: 0,
  },
  padding: 0,
}));

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: 0,
  marginLeft: '32px',
}));

const StyledAccordionList: React.FC<StyledAccordionListProps> = ({
  items,
  showArrow = true,
  onPanelChange,
  expandedPanelIds,
  defaultExpanded = false,
}) => {
  const [internalExpandedPanels, setInternalExpandedPanels] = useState<string[]>(
    defaultExpanded ? items.map((_, i) => `panel${i}`) : [],
  );
  const expandedPanels = expandedPanelIds ?? internalExpandedPanels;

  const handleChange = (panelId: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    if (onPanelChange) {
      onPanelChange(panelId, isExpanded);
    } else {
      setInternalExpandedPanels((prev) =>
        isExpanded ? [...prev, panelId] : prev.filter((p) => p !== panelId),
      );
    }
  };

  return (
    <Box>
      {items.map((item, index) => {
        const panelId = `panel${index}`;
        const isExpanded = expandedPanels.includes(panelId);
        return (
          <Accordion key={panelId} expanded={isExpanded} onChange={handleChange(panelId)}>
            <AccordionSummary
              expandIcon={
                showArrow ? <ArrowRight sx={{ fontSize: '1.5rem', fill: '#474B4C' }} /> : null
              }
              aria-controls={`${panelId}-content`}
              id={`${panelId}-header`}
            >
              {item.title}
            </AccordionSummary>
            <AccordionDetails>{item.content}</AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default StyledAccordionList;
